import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertIcon, Box, Button, HStack, IconButton, Input, Switch, Table,
  TableContainer, Tbody, Td, Text, Th, Thead, Tr, VStack, useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import ratePlanService from '../../services/ratePlanService';
import { apiError, tierRangeLabel } from './format';

const toRows = (tiers) => (tiers || [])
  .slice()
  .sort((a, b) => a.min_nights - b.min_nights)
  .map((t) => ({
    min_nights: String(t.min_nights),
    max_nights: t.max_nights == null ? '' : String(t.max_nights),
    discount_percent: String(t.discount_percent),
    is_active: t.is_active ?? true,
  }));

// Client-side mirror of the server's checks, so problems show while
// typing instead of after Save. The server re-validates regardless.
const findProblems = (rows, t) => {
  const problems = [];
  const active = rows
    .map((r, i) => ({ ...r, i, lo: Number(r.min_nights), hi: r.max_nights === '' ? Infinity : Number(r.max_nights) }))
    .filter((r) => r.is_active);
  rows.forEach((r, i) => {
    const pct = Number(r.discount_percent);
    if (!(Number(r.min_nights) >= 1)) problems.push(t('ratePlanLosTiers.problems.minNights', { row: i + 1 }));
    if (r.max_nights !== '' && Number(r.max_nights) < Number(r.min_nights)) {
      problems.push(t('ratePlanLosTiers.problems.maxBelowMin', { row: i + 1 }));
    }
    if (!(pct > 0 && pct <= 100)) problems.push(t('ratePlanLosTiers.problems.discountRange', { row: i + 1 }));
  });
  const sorted = [...active].sort((a, b) => a.lo - b.lo);
  for (let k = 1; k < sorted.length; k += 1) {
    if (sorted[k].lo <= sorted[k - 1].hi) {
      problems.push(t('ratePlanLosTiers.problems.overlap', { first: sorted[k - 1].i + 1, second: sorted[k].i + 1 }));
    }
  }
  return problems;
};

const gapsBetween = (rows) => {
  const active = rows.filter((r) => r.is_active && Number(r.min_nights) >= 1)
    .map((r) => ({ lo: Number(r.min_nights), hi: r.max_nights === '' ? Infinity : Number(r.max_nights) }))
    .sort((a, b) => a.lo - b.lo);
  const gaps = [];
  for (let k = 1; k < active.length; k += 1) {
    if (active[k].lo > active[k - 1].hi + 1) gaps.push(`${active[k - 1].hi + 1}–${active[k].lo - 1}`);
  }
  return gaps;
};

const LosTiersPanel = ({ plan, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => { setRows(toRows(plan.los_discounts)); }, [plan]);

  const problems = useMemo(() => findProblems(rows, t), [rows, t]);
  const gaps = useMemo(() => gapsBetween(rows), [rows]);
  const dirty = JSON.stringify(rows) !== JSON.stringify(toRows(plan.los_discounts));

  const update = (i, field, value) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  const addRow = () => {
    const last = rows[rows.length - 1];
    const next = last && last.max_nights !== '' ? Number(last.max_nights) + 1 : 1;
    setRows([...rows, { min_nights: String(next), max_nights: '', discount_percent: '', is_active: true }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await ratePlanService.updatePlan(plan.id, {
        los_discounts: rows.map((r) => ({
          min_nights: Number(r.min_nights),
          max_nights: r.max_nights === '' ? null : Number(r.max_nights),
          discount_percent: r.discount_percent,
          is_active: r.is_active,
        })),
      });
      toast({ title: t('ratePlanLosTiers.toasts.saved'), status: 'success', duration: 2000 });
      onSaved(saved);
    } catch (err) {
      toast({ title: t('ratePlanLosTiers.toasts.saveFailed'), description: apiError(err), status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  if (plan.rate_unit !== 'night') {
    return (
      <Alert status="info" borderRadius="md" fontSize="sm">
        <AlertIcon />
        {plan.rate_unit === 'week'
          ? t('ratePlanLosTiers.alerts.perNightOnlyWeek')
          : t('ratePlanLosTiers.alerts.perNightOnlyMonth')}
      </Alert>
    );
  }

  return (
    <VStack align="stretch" spacing={3}>
      <Text fontSize="xs" color="gray.600" maxW="680px">
        {t('ratePlanLosTiers.panel.intro')}
      </Text>

      <TableContainer borderWidth="1px" borderRadius="md" bg="white">
        <Table size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>{t('ratePlanLosTiers.table.fromNights')}</Th>
              <Th>{t('ratePlanLosTiers.table.toNights')}</Th>
              <Th>{t('ratePlanLosTiers.table.discountPercent')}</Th>
              <Th>{t('ratePlanLosTiers.table.covers')}</Th>
              <Th>{t('ratePlanLosTiers.table.active')}</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {rows.length === 0 && (
              <Tr><Td colSpan={6}>
                <Text fontSize="sm" color="gray.500" py={2}>{t('ratePlanLosTiers.emptyState.noTiers')}</Text>
              </Td></Tr>
            )}
            {rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Tr key={i}>
                <Td><Input size="sm" w="90px" type="number" min={1} value={r.min_nights}
                  onChange={(e) => update(i, 'min_nights', e.target.value)} /></Td>
                <Td><Input size="sm" w="90px" type="number" min={1} value={r.max_nights}
                  placeholder={t('ratePlanLosTiers.table.noLimit')} onChange={(e) => update(i, 'max_nights', e.target.value)} /></Td>
                <Td><Input size="sm" w="90px" type="number" min={0} max={100} step="0.01"
                  value={r.discount_percent} onChange={(e) => update(i, 'discount_percent', e.target.value)} /></Td>
                <Td fontSize="sm">{r.min_nights ? tierRangeLabel(r) : '—'}</Td>
                <Td><Switch size="sm" isChecked={r.is_active}
                  onChange={(e) => update(i, 'is_active', e.target.checked)} /></Td>
                <Td><IconButton size="xs" variant="ghost" colorScheme="red" icon={<DeleteIcon />}
                  aria-label={t('ratePlanLosTiers.table.removeTier')} onClick={() => setRows(rows.filter((_, idx) => idx !== i))} /></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {problems.length > 0 && (
        <Alert status="error" borderRadius="md" py={2} fontSize="sm" alignItems="flex-start">
          <AlertIcon />
          <Box>{problems.map((p) => <Text key={p}>{p}</Text>)}</Box>
        </Alert>
      )}
      {problems.length === 0 && gaps.length > 0 && (
        <Alert status="warning" borderRadius="md" py={2} fontSize="sm">
          <AlertIcon />
          {t('ratePlanLosTiers.alerts.gaps', { ranges: gaps.join(', ') })}
        </Alert>
      )}

      <HStack justify="space-between">
        <Button size="sm" leftIcon={<AddIcon />} variant="outline" onClick={addRow}>{t('ratePlanLosTiers.buttons.addTier')}</Button>
        <HStack>
          {dirty && (
            <Button size="sm" variant="ghost" onClick={() => setRows(toRows(plan.los_discounts))}>
              {t('ratePlanLosTiers.buttons.discardChanges')}
            </Button>
          )}
          <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}
            isDisabled={!dirty || problems.length > 0}>
            {t('ratePlanLosTiers.buttons.saveDiscounts')}
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default LosTiersPanel;
